"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "@/components/theme";
import { useTranslations } from "next-intl";

const OPTIONS = ["light", "dark", "system"] as const;
type Option = (typeof OPTIONS)[number];

export function ThemeToggle({ align = "right" }: { align?: "left" | "right" }) {
  const t = useTranslations("Theme");
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("label")}
        aria-haspopup="menu"
        aria-expanded={open}
        className="grid h-9 w-9 place-items-center rounded-full text-bark/80 transition-colors hover:bg-forest-50 hover:text-forest-600"
      >
        <ContrastIcon />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-50 mt-2 w-40 overflow-hidden rounded-xl border border-sand bg-cream p-1 shadow-lg ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          {OPTIONS.map((opt) => {
            const active = theme === opt;
            return (
              <button
                key={opt}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(opt);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  active
                    ? "bg-forest-50 text-forest-700"
                    : "text-bark hover:bg-forest-50/60"
                }`}
              >
                <OptionIcon option={opt} />
                {t(opt)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OptionIcon({ option }: { option: Option }) {
  if (option === "light") return <SunIcon />;
  if (option === "dark") return <MoonIcon />;
  return <MonitorIcon />;
}

function ContrastIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.5 6.5 0 0 0 9.8 9.8z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect
        x="3"
        y="4"
        width="18"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M8 20h8M12 16v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
